import { Wrench, Truck, HardDrive, Package, Building2, DollarSign, Coffee, ShoppingBag, Utensils } from 'lucide-react';

export const ASSET_CATEGORIES = [
  { id: 'equipment', label: 'Equipment', icon: Wrench, color: 'blue' },
  { id: 'vehicles', label: 'Vehicles', icon: Truck, color: 'green' },
  { id: 'technology', label: 'Technology', icon: HardDrive, color: 'purple' },
  { id: 'furniture', label: 'Furniture', icon: Package, color: 'orange' },
  { id: 'buildings', label: 'Buildings', icon: Building2, color: 'red' },
  { id: 'kitchen', label: 'Kitchen', icon: Utensils, color: 'yellow' },
  { id: 'retail', label: 'Retail', icon: ShoppingBag, color: 'pink' },
  { id: 'cafe', label: 'Cafe', icon: Coffee, color: 'indigo' },
];

export const ASSET_STATUSES = {
  active: { label: 'Active', color: 'green', description: 'Asset is in use and functioning properly' },
  maintenance: { label: 'In Maintenance', color: 'orange', description: 'Asset is currently under maintenance' },
  inactive: { label: 'Inactive', color: 'red', description: 'Asset is not in use' },
  pending: { label: 'Pending', color: 'blue', description: 'Asset is awaiting deployment' },
  retired: { label: 'Retired', color: 'gray', description: 'Asset has been retired from service' },
  critical: { label: 'Critical', color: 'red', description: 'Asset requires immediate attention' }
};

export const LOCATIONS = [
  { id: 'store-123', name: 'Store #123', address: '123 Main St, City, State', type: 'retail' },
  { id: 'store-124', name: 'Store #124', address: '124 Oak St, City, State', type: 'retail' },
  { id: 'warehouse-1', name: 'Main Warehouse', address: '789 Industrial Pkwy, City, State', type: 'warehouse' },
  { id: 'hq', name: 'Headquarters', address: '456 Corporate Blvd, City, State', type: 'office' },
  { id: 'cafe-01', name: 'Downtown Cafe', address: '321 Coffee Lane, City, State', type: 'cafe' },
  { id: 'kitchen-01', name: 'Central Kitchen', address: '567 Food Ave, City, State', type: 'kitchen' }
];

export const EMPLOYEES = [
  { 
    id: 'EMP-001',
    name: 'John Smith',
    department: 'IT',
    position: 'Systems Administrator',
    email: 'john.smith@company.com',
    phone: '+1 (555) 123-4567'
  },
  { 
    id: 'EMP-002',
    name: 'Sarah Williams',
    department: 'Sales',
    position: 'Sales Manager',
    email: 'sarah.williams@company.com',
    phone: '+1 (555) 234-5678'
  },
  { 
    id: 'EMP-003',
    name: 'Mike Johnson',
    department: 'Logistics',
    position: 'Fleet Manager',
    email: 'mike.johnson@company.com',
    phone: '+1 (555) 345-6789'
  },
  // Add more employees...
];

export const MOCK_ASSETS = [
  // Location-based assets
  {
    id: "AST-001",
    name: "Commercial Refrigerator XL-5000",
    assignmentType: "location",
    location: "store-123",
    status: "active",
    category: "kitchen",
    purchaseDate: "2023-08-15",
    lastMaintenance: "2024-02-20",
    nextMaintenance: "2024-05-20",
    currentValue: 12500,
    purchaseCost: 15000,
    manufacturer: "CoolTech Industries",
    model: "XL-5000",
    serialNumber: "CT2023081500123",
    warranty: "2026-08-15",
    maintenanceHistory: [
      { date: "2024-02-20", type: "Preventive", cost: 450, description: "Regular maintenance check" },
      { date: "2023-11-15", type: "Repair", cost: 850, description: "Compressor repair" }
    ],
    documents: [
      { name: "Warranty Card", type: "pdf", size: "1.2 MB", uploadDate: "2023-08-15" },
      { name: "User Manual", type: "pdf", size: "2.8 MB", uploadDate: "2023-08-15" },
      { name: "Service History", type: "xlsx", size: "156 KB", uploadDate: "2024-02-20" }
    ],
    assignedTo: "John Smith",
    department: "Kitchen Operations",
    condition: 0.95,
    depreciationRate: 0.15,
    salvageValue: 2000,
    tags: ["Kitchen", "Cold Storage", "Critical"],
    notes: "Primary refrigeration unit for store perishables",
    insuranceInfo: {
      provider: "SafeGuard Insurance",
      policyNumber: "POL-2023-001",
      coverage: 15000,
      expiryDate: "2024-08-15"
    }
  },
  // Employee-assigned assets
  {
    id: "AST-004",
    name: "MacBook Pro 16\"",
    assignmentType: "employee",
    assignedTo: {
      id: "EMP-001",
      name: "John Smith",
      department: "IT",
      email: "john.smith@company.com"
    },
    status: "active",
    category: "technology",
    purchaseDate: "2023-11-15",
    lastMaintenance: "2024-01-20",
    nextMaintenance: "2024-07-20",
    currentValue: 2800,
    purchaseCost: 3299,
    manufacturer: "Apple",
    model: "MacBook Pro 16-inch 2023",
    serialNumber: "MAC2023111500123",
    warranty: "2026-11-15",
    maintenanceHistory: [
      { date: "2024-01-20", type: "Software Update", cost: 0, description: "OS and security updates" }
    ],
    documents: [
      { name: "Purchase Receipt", type: "pdf", size: "156 KB", uploadDate: "2023-11-15" },
      { name: "Warranty Card", type: "pdf", size: "890 KB", uploadDate: "2023-11-15" }
    ],
    department: "IT",
    condition: 0.95,
    depreciationRate: 0.25,
    salvageValue: 800,
    tags: ["Laptop", "Development", "Apple"],
    notes: "Primary development machine",
    insuranceInfo: {
      provider: "TechInsure",
      policyNumber: "TEC-2023-004",
      coverage: 3500,
      expiryDate: "2024-11-15"
    }
  },
  {
    id: "AST-005",
    name: "iPhone 14 Pro",
    assignmentType: "employee",
    assignedTo: {
      id: "EMP-002",
      name: "Sarah Williams",
      department: "Sales",
      email: "sarah.williams@company.com"
    },
    status: "active",
    category: "technology",
    purchaseDate: "2023-09-20",
    lastMaintenance: "2024-02-01",
    nextMaintenance: "2024-08-01",
    currentValue: 900,
    purchaseCost: 1099,
    manufacturer: "Apple",
    model: "iPhone 14 Pro",
    serialNumber: "IPH2023092000456",
    warranty: "2024-09-20",
    maintenanceHistory: [
      { date: "2024-02-01", type: "Software Update", cost: 0, description: "iOS update and security patches" }
    ],
    documents: [
      { name: "Device Policy", type: "pdf", size: "245 KB", uploadDate: "2023-09-20" },
      { name: "Usage Agreement", type: "pdf", size: "180 KB", uploadDate: "2023-09-20" }
    ],
    department: "Sales",
    condition: 0.9,
    depreciationRate: 0.3,
    salvageValue: 200,
    tags: ["Mobile", "Sales", "Apple"],
    notes: "Sales team communication device",
    insuranceInfo: {
      provider: "TechInsure",
      policyNumber: "TEC-2023-005",
      coverage: 1200,
      expiryDate: "2024-09-20"
    }
  },
  {
    id: "AST-006",
    name: "Dell Latitude 5520",
    assignmentType: "employee",
    assignedTo: {
      id: "EMP-003",
      name: "Mike Johnson",
      department: "Logistics",
      email: "mike.johnson@company.com"
    },
    status: "maintenance",
    category: "technology",
    purchaseDate: "2023-07-10",
    lastMaintenance: "2024-02-15",
    nextMaintenance: "2024-05-15",
    currentValue: 1200,
    purchaseCost: 1499,
    manufacturer: "Dell",
    model: "Latitude 5520",
    serialNumber: "DELL202307100789",
    warranty: "2026-07-10",
    maintenanceHistory: [
      { date: "2024-02-15", type: "Hardware Repair", cost: 150, description: "Keyboard replacement" },
      { date: "2023-12-01", type: "Software Update", cost: 0, description: "Windows update and security patches" }
    ],
    documents: [
      { name: "Service Report", type: "pdf", size: "320 KB", uploadDate: "2024-02-15" },
      { name: "Warranty Info", type: "pdf", size: "150 KB", uploadDate: "2023-07-10" }
    ],
    department: "Logistics",
    condition: 0.85,
    depreciationRate: 0.25,
    salvageValue: 300,
    tags: ["Laptop", "Logistics", "Windows"],
    notes: "Fleet management system access",
    insuranceInfo: {
      provider: "TechInsure",
      policyNumber: "TEC-2023-006",
      coverage: 1500,
      expiryDate: "2024-07-10"
    }
  }
];

// Add more mock analytics data
export const MOCK_ANALYTICS = {
  assetValueTrend: [
    { month: 'Jan', value: 2100000 },
    { month: 'Feb', value: 2200000 },
    { month: 'Mar', value: 2350000 },
    { month: 'Apr', value: 2450000 },
  ],
  maintenanceCostTrend: [
    { month: 'Jan', cost: 35000 },
    { month: 'Feb', cost: 42000 },
    { month: 'Mar', cost: 38000 },
    { month: 'Apr', cost: 45750 },
  ],
  assetDistribution: [
    { category: 'Equipment', count: 450, value: 980000 },
    { category: 'Vehicles', count: 85, value: 750000 },
    { category: 'Technology', count: 620, value: 480000 },
    { category: 'Furniture', count: 92, value: 240000 },
  ],
  maintenanceStats: {
    totalCost: 165750,
    completedMaintenance: 285,
    pendingMaintenance: 45,
    averageCost: 581.57,
    upcomingMaintenance: 12
  },
  utilizationMetrics: {
    averageUtilization: 0.87,
    underutilizedAssets: 34,
    optimallyUtilized: 892,
    overutilized: 78
  },
  financialMetrics: {
    totalAssetValue: 2450000,
    depreciationExpense: 367500,
    maintenanceROI: 2.4,
    costPerAsset: 2450
  }
};

export const DEPARTMENTS = [
  'Kitchen Operations',
  'Logistics',
  'IT',
  'Office Administration',
  'Sales',
  'Warehouse',
  'Maintenance',
  'Cafe Operations',
  'Retail Operations',
  'Quality Control'
];

export const MANUFACTURERS = [
  'CoolTech Industries',
  'VehicleCorp',
  'TechPro Solutions',
  'FurnishCo',
  'BuildRight Construction',
  'Kitchen Equipment Pro',
  'Retail Systems Inc',
  'Cafe Solutions Ltd',
  'Industrial Machinery Corp',
  'Office Systems International'
];

export const INSURANCE_PROVIDERS = [
  'SafeGuard Insurance',
  'AutoGuard Insurance',
  'TechInsure',
  'Commercial Asset Protection',
  'Industrial Shield Insurance'
];

export const MAINTENANCE_TYPES = [
  'Preventive',
  'Corrective',
  'Condition-based',
  'Predictive',
  'Emergency',
  'Routine Inspection',
  'Software Update',
  'Hardware Check',
  'Calibration',
  'Safety Inspection'
];